import { lazy } from 'react';
import { getHomePaths } from "./homePaths";
import { ItemRoute } from "@/app/common/types/commonTypes";

// Lazy load page components
const Dental = lazy(() => import("@/app/dental/pages/Dental"));
const Doctors = lazy(() => import("@/app/doctors/pages/Doctors"));
const Optical = lazy(() => import("@/app/optical/pages/Optical"));
const Theatre = lazy(() => import("@/app/theatre/pages/Theatre"));
const Pharmacy = lazy(() => import("@/app/pharmacy/pages/Pharmacy"));
const Inventory = lazy(() => import("@/app/inventory/pages/Inventory"));
const Cardiology = lazy(() => import("@/app/cardiology/pages/Cardiology"));
const Laboratory = lazy(() => import("@/app/laboratory/pages/Laboratory"));
const Chats = lazy(() => import("@/app/common/communication/chats/pages/Chats"));
const Queues = lazy(() => import("@/app/common/communication/queues/pages/Queues"));

// Lazy load route configurations asynchronously
export const loadHomeRoutes = async () => {
  const { nurseRoutes } = await import("@/app/nurse/routes/nurseRoutes");
  const { financeRoutes } = await import("@/app/finance/routes/financeRoutes");
  const { clinicalRoutes } = await import("@/app/clinical/routes/clinicalRoutes");
  const { createItemRoutes } = await import("@/app/common/utils/createItemRoutes");
  const { reportRoutes } = await import("@/app/reports/common/routes/reportRoutes");
  const { radiologyRoutes } = await import("@/app/radiology/routes/radiologyRoutes");
  const { dashboardRoutes } = await import("@/app/dashboard/routes/dashboardRoutes");
  const { integrationRoutes } = await import("@/app/integrations/routes/integrationRoutes");
  const { registrationRoutes } = await import("@/app/registration/routes/registrationRoutes");
  const { theatreRoutes } = await import("@/app/theatre/routes/theatreRoutes");
  const { pharmacyRoutes } = await import("@/app/pharmacy/routes/pharmacyRoutes");
  const { doctorRoutes } = await import("@/app/doctors/routes/doctorRoutes");
  const { inventoryRoutes } = await import("@/app/inventory/routes/inventoryRoutes");
  const { communicationPaths } = await import("@/app/common/communication/routes/communicationPaths");
  const homePaths = await getHomePaths();

  const homeRoutes: ItemRoute[] = [
    ...dashboardRoutes,
    ...registrationRoutes,
    ...doctorRoutes,
    ...inventoryRoutes,
    ...theatreRoutes,
    ...pharmacyRoutes,
    ...financeRoutes,
    ...clinicalRoutes,
    ...nurseRoutes,
    ...radiologyRoutes,
    ...reportRoutes,
    ...integrationRoutes,

    { path: homePaths.doctors.path, element: Doctors },
    { path: homePaths.pharmacy.path, element: Pharmacy },
    { path: homePaths.laboratory.path, element: Laboratory },
    { path: homePaths.theatre.path, element: Theatre },
    { path: homePaths.dental.path, element: Dental },
    { path: homePaths.cardiology.path, element: Cardiology },
    { path: homePaths.optical.path, element: Optical },
    { path: homePaths.inventory.path, element: Inventory },

    { path: communicationPaths.queues.path, element: Queues },
    { path: communicationPaths.chats.path, element: Chats },
  ];

  return createItemRoutes(homeRoutes);
};

// Cached result
let cachedHomeRoutesConfig: ItemRoute[] | undefined = undefined;

export const getHomeRoutesConfig = async (): Promise<ItemRoute[]> => {
  if (cachedHomeRoutesConfig) {
    return cachedHomeRoutesConfig;
  }
  const routes = await loadHomeRoutes();
  if (!routes) {
    return [];
  }
  cachedHomeRoutesConfig = routes;
  return cachedHomeRoutesConfig;
};

// For backwards compatibility with old imports
export const homeRoutesConfig: ItemRoute[] = [];
